// Copyright ©️ 2026 eVolve MEP, LLC

import type { CustomPropertyTypeName } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import { AddEditCustomPropertyPopover } from 'components/Mantine/CustomProperties/AddEditCustomPropertyPopover';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNil } from 'helpers/isNotNil';
import { useHasAccess } from 'hooks/user/useHasAccess';

import { CustomPropertiesContents } from './CustomPropertiesContents';
import { ManageCustomPropertiesButton } from './ManageCustomPropertiesButton';

type Props = {
  /** The type of custom properties managed by this modal (i.e. Work Order, Task, etc.) */
  customPropertyTypeName: CustomPropertyTypeName;
  keepMounted?: boolean;
  disabled?: boolean;
} & (
  | {
      /**
       * Opened state.
       * When provided, the modal will be controlled by the parent component and will not render its own {@link ManageCustomPropertiesButton}
       **/
      opened: boolean;
      /** Callback fired when the modal requests to be closed. */
      onClose: () => void;
      actionIcon?: never;
    }
  | {
      opened?: never;
      onClose?: never;
      /** When true, the included {@link ManageCustomPropertiesButton} will display as an action icon. */
      actionIcon?: boolean;
    }
);

/** Modal used to manage custom properties for a given custom property type. */
export const EditCustomPropertiesModal = ({
  customPropertyTypeName,
  keepMounted,
  opened: parentOpened,
  onClose,
  disabled,
  actionIcon,
}: Props) => {
  const hasCustomPropertiesAccess = useHasAccess('customProperties', 'write');

  const [opened, setOpened] = useUncontrolled({
    value: parentOpened,
    defaultValue: false,
    onChange: (o) => {
      if (!o) onClose?.();
    },
  });
  const { loading } = useCustomProperties(customPropertyTypeName);

  if (!hasCustomPropertiesAccess) return null;

  return (
    <>
      {isNil(parentOpened) && (
        <ManageCustomPropertiesButton onClick={() => setOpened(true)} disabled={disabled} actionIcon={actionIcon} />
      )}
      <WrappedModal
        title="Manage custom properties"
        subtitle={`${customPropertyTypeName}s`}
        size="lg"
        opened={opened}
        onClose={() => {
          onClose?.();
          setOpened(false);
        }}
        hideModalButtons
        loading={loading}
        keepMounted={keepMounted}
      >
        <CustomPropertiesContents
          customPropertyTypeName={customPropertyTypeName}
          topRightSection={<AddEditCustomPropertyPopover customPropertyTypeName={customPropertyTypeName} />}
        />
      </WrappedModal>
    </>
  );
};
